import React, { useEffect, useState } from "react";
import { Box, Alert, Typography } from "@mui/material";
import {
  LineChart, Line, BarChart, Bar, ComposedChart, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend,
} from "recharts";
import SectionLayout from "../components/SectionLayout";
import ChartCard from "../components/charts/ChartCard";
import { Grafico, ejes, ejeTemporal, tooltipStyle, legendProps, barCursor, ChartLoading, ChartError } from "../components/charts/chartTheme";
import { loadCSV, DATA } from "../services/csvLoader";
import { BRAND, SECTION_COLORS } from "../theme";

const MORADO = SECTION_COLORS.migracion.main;

const INDICADORES = [
  { id: "permisos_total",    label: "Permisos de residencia totales" },
  { id: "permisos_trabajo",  label: "Permisos — Actividades remuneradas" },
  { id: "permisos_familia",  label: "Permisos — Reunificación familiar" },
  { id: "refugio",           label: "Solicitudes de refugio" },
  { id: "pasos_no_hab",      label: "Ingresos pasos no habilitados" },
  { id: "denuncias",         label: "Denuncias administrativas" },
  { id: "expulsiones_mat",   label: "Expulsiones materializadas" },
  { id: "salidas",           label: "Salidas voluntarias" },
  { id: "poblacion_migrante",label: "Población migrante residente" },
  { id: "indice_masc",       label: "Índice de masculinidad" },
  { id: "dependencia",       label: "Índice de dependencia" },
  { id: "envejecimiento",    label: "Índice de envejecimiento" },
  { id: "soporte",           label: "Tasa de soporte potencial" },
];

function useCSV(url) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    loadCSV(url)
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { setError(String(e)); setLoading(false); });
  }, [url]);
  return { data, loading, error };
}

/* Permisos otorgados por año y tipo */
function GraficoPermisos({ setCSV }) {
  const { data, loading, error } = useCSV(DATA.migracion.permisosAnuales);
  useEffect(() => { if (data.length) setCSV(data); }, [data, setCSV]);
  if (loading) return <ChartLoading />;
  if (error) return <Alert severity="warning">Error: {error}</Alert>;

  const mapped = data
    .filter((r) => r.anio >= 2010)
    .map((r) => ({ año: r.anio, Temporales: r.temporales, Definitivas: r.definitivas }));

  return (
    <Grafico width="100%" height={300}>
      <BarChart data={mapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="año" {...ejes.x} />
        <YAxis {...ejes.y} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
        <Tooltip {...tooltipStyle} cursor={barCursor} formatter={(v) => v.toLocaleString("es-CL")} />
        <Legend {...legendProps} />
        <Bar dataKey="Temporales" stackId="a" fill={MORADO} maxBarSize={56} />
        <Bar dataKey="Definitivas" stackId="a" fill={BRAND.nodoBlue} radius={[4, 4, 0, 0]} maxBarSize={56} />
      </BarChart>
    </Grafico>
  );
}

/* Permisos RT por subcategoría (filtro por nombres) */
function GraficoSubcategoria({ subcategorias, colores, desde = 2010, setCSV }) {
  const { data, loading, error } = useCSV(DATA.migracion.rtSubcategorias);
  // La clave string evita re-cálculos (y bucles de render) cuando el padre
  // recrea el array subcategorias en cada render.
  const clave = subcategorias.join("|");
  const filtradas = React.useMemo(
    () => data.filter((r) => subcategorias.includes(r.subcategoria) && r.anio >= desde),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, clave, desde]
  );
  useEffect(() => { if (filtradas.length) setCSV(filtradas); }, [filtradas, setCSV]);
  if (loading) return <ChartLoading />;
  if (error) return <Alert severity="warning">Error: {error}</Alert>;

  const años = [...new Set(filtradas.map((r) => r.anio))].sort();
  const mapped = años.map((a) => {
    const fila = { año: a };
    for (const s of subcategorias) {
      const r = filtradas.find((x) => x.anio === a && x.subcategoria === s);
      fila[s] = r ? r.otorgados : 0;
    }
    return fila;
  });

  return (
    <Grafico width="100%" height={300}>
      <LineChart data={mapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="año" {...ejes.x} />
        <YAxis {...ejes.y} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
        <Tooltip {...tooltipStyle} formatter={(v) => v.toLocaleString("es-CL")} />
        <Legend {...legendProps} />
        {subcategorias.map((s, i) => (
          <Line key={s} type="monotone" dataKey={s} stroke={colores[i]} dot strokeWidth={2} />
        ))}
      </LineChart>
    </Grafico>
  );
}

/* Refugio: solicitudes vs reconocimientos vs rechazos */
function GraficoRefugio({ setCSV }) {
  const { data, loading, error } = useCSV(DATA.migracion.refugio);
  useEffect(() => { if (data.length) setCSV(data); }, [data, setCSV]);
  if (loading) return <ChartLoading />;
  if (error) return <Alert severity="warning">Error: {error}</Alert>;

  const mapped = data.map((r) => ({
    año: r.anio, Solicitudes: r.solicitudes, Reconocimientos: r.reconocimientos, Rechazos: r.rechazos,
  }));

  return (
    <Grafico width="100%" height={300}>
      <LineChart data={mapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="año" {...ejes.x} />
        <YAxis {...ejes.y} />
        <Tooltip {...tooltipStyle} formatter={(v) => v.toLocaleString("es-CL")} />
        <Legend {...legendProps} />
        <Line type="monotone" dataKey="Solicitudes" stroke={MORADO} dot strokeWidth={2} />
        <Line type="monotone" dataKey="Reconocimientos" stroke="#0E9594" dot strokeWidth={2} />
        <Line type="monotone" dataKey="Rechazos" stroke={BRAND.fesRed} dot strokeWidth={2} />
      </LineChart>
    </Grafico>
  );
}

/* Población migrante por sexo */
function GraficoPoblacion({ setCSV }) {
  const { data, loading, error } = useCSV(DATA.migracion.poblacion);
  useEffect(() => { if (data.length) setCSV(data); }, [data, setCSV]);
  if (loading) return <ChartLoading />;
  if (error) return <Alert severity="warning">Error: {error}</Alert>;

  const mapped = data.map((r) => ({ año: r.anio, Hombres: r.hombres, Mujeres: r.mujeres }));

  return (
    <Grafico width="100%" height={300}>
      <BarChart data={mapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="año" {...ejes.x} />
        <YAxis {...ejes.y} tickFormatter={(v) => `${(v / 1e6).toFixed(1)}M`} />
        <Tooltip {...tooltipStyle} cursor={barCursor} formatter={(v) => v.toLocaleString("es-CL")} />
        <Legend {...legendProps} />
        <Bar dataKey="Hombres" stackId="a" fill={BRAND.nodoBlue} maxBarSize={56} />
        <Bar dataKey="Mujeres" stackId="a" fill={BRAND.fesRed} radius={[4, 4, 0, 0]} maxBarSize={56} />
      </BarChart>
    </Grafico>
  );
}

/* Línea genérica sobre indices_demograficos.csv */
function GraficoIndice({ campos, etiquetas, colores, setCSV }) {
  const { data, loading, error } = useCSV(DATA.migracion.indicesDemograficos);
  useEffect(() => { if (data.length) setCSV(data); }, [data, setCSV]);
  if (loading) return <ChartLoading />;
  if (error) return <Alert severity="warning">Error: {error}</Alert>;

  const mapped = data.map((r) => {
    const fila = { año: r.anio };
    campos.forEach((c, i) => { fila[etiquetas[i]] = r[c]; });
    return fila;
  });

  return (
    <Grafico width="100%" height={280}>
      <LineChart data={mapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="año" {...ejes.x} />
        <YAxis {...ejes.y} domain={["auto", "auto"]} />
        <Tooltip {...tooltipStyle} />
        <Legend {...legendProps} />
        {etiquetas.map((e, i) => (
          <Line key={e} type="monotone" dataKey={e} stroke={colores[i]} dot strokeWidth={2} />
        ))}
      </LineChart>
    </Grafico>
  );
}

/* Expulsiones materializadas por tipo (administrativa vs. judicial) */
function GraficoExpulsiones({ setCSV }) {
  const { data, loading, error } = useCSV(DATA.migracion.expulsionesMaterializadas);
  useEffect(() => { if (data.length) setCSV(data); }, [data, setCSV]);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const mapped = data.map((r) => ({
    año: r.anio, Administrativa: r.administrativa, Judicial: r.judicial,
  }));

  return (
    <Grafico width="100%" height={300}>
      <BarChart data={mapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="año" {...ejes.x} />
        <YAxis {...ejes.y} />
        <Tooltip {...tooltipStyle} cursor={barCursor} formatter={(v) => v.toLocaleString("es-CL")} />
        <Legend {...legendProps} />
        <Bar dataKey="Administrativa" stackId="a" fill={MORADO} maxBarSize={56} />
        <Bar dataKey="Judicial" stackId="a" fill={BRAND.fesRed} radius={[4, 4, 0, 0]} maxBarSize={56} />
      </BarChart>
    </Grafico>
  );
}

/* Ingresos por pasos no habilitados: denuncias (barras) + reconducidos (línea, eje secundario) */
function GraficoPasosNoHabilitados({ setCSV }) {
  const { data, loading, error } = useCSV(DATA.migracion.pasosNoHabilitados);
  useEffect(() => { if (data.length) setCSV(data); }, [data, setCSV]);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const mapped = data.map((r) => ({
    año: r.anio, "Denuncias": r.denuncias, "Reconducidos": r.reconducidos,
  }));

  return (
    <Grafico width="100%" height={300}>
      <ComposedChart data={mapped} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="año" {...ejes.x} />
        <YAxis yAxisId="izq" {...ejes.y} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
        <YAxis yAxisId="der" orientation="right" {...ejes.y} />
        <Tooltip {...tooltipStyle} cursor={barCursor} formatter={(v) => v.toLocaleString("es-CL")} />
        <Legend {...legendProps} />
        <Bar yAxisId="izq" dataKey="Denuncias" fill={MORADO} radius={[4, 4, 0, 0]} maxBarSize={40} />
        <Line yAxisId="der" type="monotone" dataKey="Reconducidos" stroke={BRAND.fesRed} strokeWidth={2} dot />
      </ComposedChart>
    </Grafico>
  );
}

/* Denuncias administrativas totales (todas las infracciones migratorias) */
function GraficoDenunciasAdministrativas({ setCSV }) {
  const { data, loading, error } = useCSV(DATA.migracion.denunciasAdministrativas);
  useEffect(() => { if (data.length) setCSV(data); }, [data, setCSV]);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const mapped = data.map((r) => ({ año: r.anio, "Denuncias administrativas": r.total }));

  return (
    <Grafico width="100%" height={280}>
      <BarChart data={mapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="año" {...ejes.x} />
        <YAxis {...ejes.y} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
        <Tooltip {...tooltipStyle} cursor={barCursor} formatter={(v) => v.toLocaleString("es-CL")} />
        <Bar dataKey="Denuncias administrativas" fill={MORADO} radius={[4, 4, 0, 0]} maxBarSize={56} />
      </BarChart>
    </Grafico>
  );
}

/* Salidas voluntarias con Autorización de Salida (SERMIG), serie mensual */
function GraficoSalidasVoluntarias({ setCSV }) {
  const { data, loading, error } = useCSV(DATA.migracion.salidasVoluntarias);
  useEffect(() => { if (data.length) setCSV(data); }, [data, setCSV]);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const mapped = data.map((r) => ({ fecha: r.fecha, "Salidas voluntarias": r.salidas }));

  return (
    <Grafico width="100%" height={280}>
      <LineChart data={mapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="fecha" {...ejes.x} {...ejeTemporal} tickCount={8} interval="preserveStartEnd" />
        <YAxis {...ejes.y} />
        <Tooltip {...tooltipStyle} formatter={(v) => v.toLocaleString("es-CL")} />
        <Line type="monotone" dataKey="Salidas voluntarias" stroke={MORADO} dot={false} strokeWidth={2} />
      </LineChart>
    </Grafico>
  );
}

/** Envuelve un gráfico para inyectar sus datos al botón de descarga CSV */
function ConCSV({ render, ...cardProps }) {
  const [csv, setCSV] = useState([]);
  return (
    <ChartCard {...cardProps} csvData={csv.length ? csv : undefined}>
      {render(setCSV)}
    </ChartCard>
  );
}

export default function Migracion() {
  return (
    <SectionLayout
      title="Migración"
      description="Indicadores de la operación migratoria y perfil sociodemográfico de la población migrante en Chile. Fuentes: SERMIG, PDI e INE."
      tag="Operación Migratoria y Demografía"
      palette={SECTION_COLORS.migracion}
      indicadores={INDICADORES}
    >
      {/* SUBCATEGORÍA: OPERACIÓN MIGRATORIA */}
      <Typography variant="overline" color="text.secondary" fontWeight={700} mb={2} display="block">
        Operación Migratoria
      </Typography>

      <Box id="permisos_total">
        <ConCSV
          title="Permisos de residencia otorgados"
          subtitle="Permisos de residencia temporal y definitiva otorgados por año. Registros administrativos SERMIG (datos abiertos)."
          fuente="SERMIG — Servicio Nacional de Migraciones"
          tag="Semestral"
          csvFilename="permisos_residencia_total"
          pngFilename="permisos_residencia_total"
          render={(setCSV) => (
            <>
              <GraficoPermisos setCSV={setCSV} />
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                Desde 2021 los permisos los otorga el SERMIG (Ley 21.325); los datos anteriores provienen del
                Departamento de Extranjería y Migración, con metodología distinta.
              </Typography>
            </>
          )}
        />
      </Box>

      <Box id="permisos_trabajo">
        <ConCSV
          title="Permisos de residencia — Actividades Lícitas Remuneradas"
          subtitle="Permisos temporales otorgados en la subcategoría Actividades Lícitas Remuneradas, por año."
          fuente="SERMIG"
          tag="Semestral"
          csvFilename="permisos_trabajo"
          pngFilename="permisos_trabajo"
          render={(setCSV) => (
            <>
              <GraficoSubcategoria
                subcategorias={["Actividades lícitas remuneradas"]}
                colores={[MORADO]}
                desde={2022}
                setCSV={setCSV}
              />
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                La serie parte en 2022: el Decreto 177, vigente desde el 14 de mayo de 2022, aplicó
                la Ley 21.325 y desde esa fecha todo permiso se otorga bajo "Actividades lícitas
                remuneradas", aunque la solicitud haya ingresado bajo la ley anterior. Por eso no se
                empalma con "Motivos Laborales", la subcategoría equivalente previa.
              </Typography>
            </>
          )}
        />
      </Box>

      <Box id="permisos_familia">
        <ConCSV
          title="Permisos de residencia — Reunificación Familiar"
          subtitle="Permisos temporales otorgados en la subcategoría Reunificación Familiar, por año."
          fuente="SERMIG"
          tag="Semestral"
          csvFilename="permisos_familia"
          pngFilename="permisos_familia"
          render={(setCSV) => (
            <>
              <GraficoSubcategoria
                subcategorias={["Reunificación familiar"]}
                colores={[MORADO]}
                desde={2022}
                setCSV={setCSV}
              />
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                Igual que en actividades remuneradas: la serie parte en 2022 con el Decreto 177 y no
                se empalma con "Vínculo con Chileno/Pede", la subcategoría equivalente previa a la
                Ley 21.325.
              </Typography>
            </>
          )}
        />
      </Box>

      <Box id="refugio">
        <ConCSV
          title="Solicitudes y reconocimientos de la condición de refugiado/a"
          subtitle="Solicitudes formalizadas, reconocimientos y rechazos por año."
          fuente="SERMIG"
          tag="Semestral"
          csvFilename="refugio"
          pngFilename="refugio"
          render={(setCSV) => <GraficoRefugio setCSV={setCSV} />}
        />
      </Box>

      <Box id="pasos_no_hab">
        <ConCSV
          title="Ingresos por pasos no habilitados"
          subtitle="Denuncias por ingreso en paso no habilitado y personas reconducidas en frontera (Art. 131, Ley 21.325), por año."
          fuente="PDI, Jefatura Nacional de Migraciones y Policía Internacional — Informe Estadístico Migratorio"
          tag="Anual"
          csvFilename="pasos_no_habilitados"
          pngFilename="pasos_no_habilitados"
          render={(setCSV) => (
            <>
              <GraficoPasosNoHabilitados setCSV={setCSV} />
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                2026 es un año parcial (corte al 11 de abril). Cifra 2025 de denuncias cruzada con
                reportaje de 24horas.cl (PDI vía Ley de Transparencia): coinciden en 26.275.
              </Typography>
            </>
          )}
        />
      </Box>

      <Box id="denuncias">
        <ConCSV
          title="Denuncias administrativas por infracción migratoria"
          subtitle="Total de denuncias administrativas informadas al Servicio Nacional de Migraciones (todas las infracciones, no solo ingreso por paso no habilitado)."
          fuente="PDI, Jefatura Nacional de Migraciones y Policía Internacional — Informe Estadístico Migratorio"
          tag="Anual"
          csvFilename="denuncias_administrativas"
          pngFilename="denuncias_administrativas"
          render={(setCSV) => (
            <>
              <GraficoDenunciasAdministrativas setCSV={setCSV} />
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                2026 es un año parcial (corte al 11 de abril).
              </Typography>
            </>
          )}
        />
      </Box>

      <Box id="expulsiones_mat">
        <ConCSV
          title="Expulsiones materializadas"
          subtitle="Expulsiones administrativas y judiciales efectivamente ejecutadas por la PDI, por año."
          fuente="PDI, Jefatura Nacional de Migraciones y Policía Internacional — Informe Estadístico Migratorio"
          tag="Anual"
          csvFilename="expulsiones_materializadas"
          pngFilename="expulsiones_materializadas"
          render={(setCSV) => (
            <>
              <GraficoExpulsiones setCSV={setCSV} />
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                2026 es un año parcial (corte al 11 de abril). Judiciales: pena sustitutiva (Art. 34,
                Ley 18.216); administrativas: dictadas por el SERMIG y ejecutadas por la PDI.
              </Typography>
            </>
          )}
        />
      </Box>

      <Box id="salidas">
        <ConCSV
          title="Salidas voluntarias con Autorización de Salida"
          subtitle="Autorizaciones emitidas por el Servicio Nacional de Migraciones para la salida voluntaria del país, serie mensual."
          fuente="SERMIG, vía PDI — Informe Estadístico Migratorio"
          tag="Mensual"
          csvFilename="salidas_voluntarias"
          pngFilename="salidas_voluntarias"
          render={(setCSV) => <GraficoSalidasVoluntarias setCSV={setCSV} />}
        />
      </Box>

      {/* SUBCATEGORÍA: SOCIODEMOGRÁFICA */}
      <Typography variant="overline" color="text.secondary" fontWeight={700} mt={3} mb={2} display="block">
        Perfil Sociodemográfico
      </Typography>

      <Box id="poblacion_migrante">
        <ConCSV
          title="Población migrante internacional residente en Chile"
          subtitle="Estimación de personas extranjeras residentes, por sexo. Estimación de Población Extranjera (EPE), INE y SERMIG."
          fuente="INE / SERMIG — Estimación de Población Extranjera"
          tag="Anual"
          csvFilename="poblacion_migrante"
          pngFilename="poblacion_migrante"
          render={(setCSV) => <GraficoPoblacion setCSV={setCSV} />}
        />
      </Box>

      <Box id="indice_masc">
        <ConCSV
          title="Índice de masculinidad"
          subtitle="Hombres por cada 100 mujeres en la población migrante residente."
          fuente="INE / SERMIG — EPE"
          tag="Anual"
          csvFilename="indice_masculinidad"
          pngFilename="indice_masculinidad"
          render={(setCSV) => (
            <GraficoIndice
              campos={["indice_masculinidad"]}
              etiquetas={["Hombres por cada 100 mujeres"]}
              colores={[MORADO]}
              setCSV={setCSV}
            />
          )}
        />
      </Box>

      <Box id="dependencia">
        <ConCSV
          title="Índice de dependencia total / joven / vejez"
          subtitle="Personas dependientes (menores de 15 y mayores de 65) por cada 100 en edad de trabajar, en la población migrante."
          fuente="INE / SERMIG — EPE"
          tag="Anual"
          csvFilename="indice_dependencia"
          pngFilename="indice_dependencia"
          render={(setCSV) => (
            <GraficoIndice
              campos={["dependencia_total", "dependencia_joven", "dependencia_vejez"]}
              etiquetas={["Total", "Joven (<15)", "Vejez (>65)"]}
              colores={[MORADO, "#0E9594", BRAND.fesRed]}
              setCSV={setCSV}
            />
          )}
        />
      </Box>

      <Box id="envejecimiento">
        <ConCSV
          title="Índice de envejecimiento"
          subtitle="Adultos mayores (65+) por cada 100 menores de 15 años, en la población migrante."
          fuente="INE / SERMIG — EPE"
          tag="Anual"
          csvFilename="indice_envejecimiento"
          pngFilename="indice_envejecimiento"
          render={(setCSV) => (
            <GraficoIndice
              campos={["indice_envejecimiento"]}
              etiquetas={["Índice de envejecimiento"]}
              colores={[BRAND.fesRed]}
              setCSV={setCSV}
            />
          )}
        />
      </Box>

      <Box id="soporte">
        <ConCSV
          title="Tasa de soporte potencial"
          subtitle="Personas en edad activa (15–64) por cada adulto mayor de 65 años, en la población migrante."
          fuente="INE / SERMIG — EPE"
          tag="Anual"
          csvFilename="tasa_soporte"
          pngFilename="tasa_soporte"
          render={(setCSV) => (
            <GraficoIndice
              campos={["tasa_soporte"]}
              etiquetas={["Activos por adulto mayor"]}
              colores={[BRAND.nodoBlue]}
              setCSV={setCSV}
            />
          )}
        />
      </Box>
    </SectionLayout>
  );
}
